import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api, errorMessage } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useChartTheme } from "@/hooks/useChartTheme";
import { PageHeader } from "@/components/PageHeader";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { formatKr, formatMonth } from "@/lib/format";
import { BarChart, Bar, XAxis, YAxis, Tooltip as RTooltip, ResponsiveContainer, CartesianGrid, Legend } from "recharts";
import { EmptyState } from "@/components/EmptyState";
import { CalendarRange } from "lucide-react";

const ChartTooltip = ({ active, payload, label }) => {
  if (!active || !payload?.length) return null;
  const d = payload[0].payload;
  return (
    <div className="rounded-lg border bg-card p-3 text-sm shadow-md">
      <p className="font-medium">{formatMonth(label)}</p>
      <p className="tnum">{d.count} regninger · {formatKr(d.total)}</p>
      <p className="text-xs text-muted-foreground tnum">Ubetalt {formatKr(d.unpaid)} · Betalt {formatKr(d.paid)} · AvtaleGiro {formatKr(d.avtalegiro)}</p>
    </div>
  );
};

export default function MonthlyPage() {
  const { canWrite } = useAuth();
  const chart = useChartTheme();
  const [data, setData] = useState(null);
  const [error, setError] = useState("");

  useEffect(() => {
    api.get("/monthly", { params: { months_back: 6, months_forward: 6 } }).then((r) => setData(r.data)).catch((e) => setError(errorMessage(e)));
  }, []);

  if (error) return <EmptyState title="Kunne ikke laste månedsoversikten" description={error} />;
  if (!data) return <div className="space-y-4"><Skeleton className="h-10 w-64" /><Skeleton className="h-80" /><Skeleton className="h-64" /></div>;

  const upcoming = data.months.filter((m) => m.month >= new Date().toISOString().slice(0, 7));
  const upcomingTotal = upcoming.reduce((a, m) => a + m.unpaid + m.avtalegiro, 0);
  const hasAny = data.months.some((m) => m.count > 0);
  const legend = [["Ubetalt", chart.unpaid], ["Betalt", chart.paid], ["AvtaleGiro", chart.avtalegiro]];

  return (
    <div className="space-y-6">
      <PageHeader title="Månedsoversikt" description="Forventede utgifter fremover og historikk per måned, basert på forfallsdato." />

      {!hasAny ? <EmptyState icon={CalendarRange} title="Ingen regninger ennå" description="Når du har lastet opp regninger, ser du forventede kostnader per måned her." actionLabel={canWrite ? "Last opp" : undefined} actionTo={canWrite ? "/last-opp" : undefined} /> : null}

      <div className="grid gap-4 md:grid-cols-3">
        <Card><CardContent className="p-5"><p className="text-sm text-muted-foreground">Ubetalt og AvtaleGiro fremover</p><p className="font-heading text-2xl font-semibold tnum mt-1" data-testid="monthly-upcoming-total">{formatKr(upcomingTotal)}</p></CardContent></Card>
        <Card><CardContent className="p-5"><p className="text-sm text-muted-foreground">Denne måneden</p><p className="font-heading text-2xl font-semibold tnum mt-1">{formatKr(data.months.find((m) => m.is_current)?.total || 0)}</p><p className="text-xs text-muted-foreground">{data.months.find((m) => m.is_current)?.count || 0} regninger</p></CardContent></Card>
        <Card><CardContent className="p-5"><p className="text-sm text-muted-foreground">Snitt per måned (siste 6)</p><p className="font-heading text-2xl font-semibold tnum mt-1">{formatKr(data.months.filter((m) => m.month < new Date().toISOString().slice(0, 7)).slice(-6).reduce((a, m) => a + m.total, 0) / 6)}</p></CardContent></Card>
      </div>

      <Card>
        <CardHeader><CardTitle className="text-base">Regninger per måned</CardTitle><CardDescription>Fordelt på betalt, ubetalt og AvtaleGiro.</CardDescription></CardHeader>
        <CardContent>
          <div className="h-80" data-testid="monthly-chart">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={data.months} margin={{ top: 8, right: 8, left: -8, bottom: 0 }}>
                <CartesianGrid stroke={chart.grid} vertical={false} />
                <XAxis dataKey="month" tickFormatter={(m) => formatMonth(m, true)} tick={{ fill: chart.tick, fontSize: 12 }} axisLine={false} tickLine={false} />
                <YAxis tick={{ fill: chart.tick, fontSize: 12 }} axisLine={false} tickLine={false} tickFormatter={(v) => (v >= 1000 ? `${Math.round(v / 1000)}k` : v)} />
                <RTooltip content={<ChartTooltip />} cursor={{ fill: chart.cursor }} />
                <Legend content={() => (
                  <ul className="flex justify-center gap-5 pt-3 text-sm">
                    {legend.map(([l, c]) => (
                      <li key={l} className="flex items-center gap-1.5"><span className="h-2.5 w-2.5 rounded-full" style={{ background: c }} />{l}</li>
                    ))}
                  </ul>
                )} />
                <Bar dataKey="unpaid" name="Ubetalt" stackId="a" fill={chart.unpaid} maxBarSize={44} />
                <Bar dataKey="paid" name="Betalt" stackId="a" fill={chart.paid} maxBarSize={44} />
                <Bar dataKey="avtalegiro" name="AvtaleGiro" stackId="a" fill={chart.avtalegiro} radius={[6, 6, 0, 0]} maxBarSize={44} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </CardContent>
      </Card>

      <div className="grid gap-4 lg:grid-cols-2">
        <Card>
          <CardHeader><CardTitle className="text-base">Per måned</CardTitle></CardHeader>
          <CardContent className="p-0">
            <Table>
              <TableHeader><TableRow><TableHead>Måned</TableHead><TableHead className="text-right">Antall</TableHead><TableHead className="text-right">Ubetalt</TableHead><TableHead className="text-right">Totalt</TableHead></TableRow></TableHeader>
              <TableBody>
                {data.months.map((m) => (
                  <TableRow key={m.month} className={m.is_current ? "bg-accent/40" : ""}>
                    <TableCell><Link to={`/fakturaer?date_from=${m.month}-01&date_to=${m.month}-31`} className="hover:underline font-medium" data-testid={`monthly-row-${m.month}`}>{formatMonth(m.month)}</Link></TableCell>
                    <TableCell className="text-right tnum">{m.count}</TableCell>
                    <TableCell className="text-right tnum">{formatKr(m.unpaid + m.avtalegiro)}</TableCell>
                    <TableCell className="text-right tnum font-medium">{formatKr(m.total)}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </CardContent>
        </Card>
        <Card>
          <CardHeader><CardTitle className="text-base">Per kategori</CardTitle><CardDescription>Samme periode, fordelt på kategori.</CardDescription></CardHeader>
          <CardContent className="space-y-3">
            {data.categories.length === 0 ? <p className="text-sm text-muted-foreground">Ingen data.</p> : data.categories.map((c) => {
              const max = data.categories[0]?.total || 1;
              return (
                <Link key={c.category_id} to={`/fakturaer?category_id=${c.category_id}`} className="block group" data-testid={`monthly-category-${c.category_id}`}>
                  <div className="flex items-center justify-between text-sm mb-1">
                    <span className="flex items-center gap-2 font-medium group-hover:underline"><span className="h-2.5 w-2.5 rounded-full" style={{ background: c.color }} />{c.name}</span>
                    <span className="tnum">{formatKr(c.total)} <span className="text-muted-foreground">· {c.count}</span></span>
                  </div>
                  <div className="h-2 rounded-full bg-secondary overflow-hidden"><div className="h-full rounded-full" style={{ width: `${Math.max(3, (c.total / max) * 100)}%`, background: c.color }} /></div>
                </Link>
              );
            })}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
